

"use client"
import { useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { useEffect, useMemo } from "react"

const SelectUserType = () => {
  const navigate = useNavigate()
  const { t, i18n } = useTranslation()

  const isRTL = typeof i18n.dir === 'function' ? i18n.dir() === 'rtl' : i18n.language === 'ar'

  useEffect(() => {
    const applyDirection = (lng) => {
      const direction = typeof i18n.dir === 'function' ? i18n.dir(lng) : (lng === 'ar' ? 'rtl' : 'ltr')
      document.documentElement.dir = direction
      document.documentElement.lang = lng
    }

    applyDirection(i18n.language)
    i18n.on('languageChanged', applyDirection)

    return () => {
      i18n.off('languageChanged', applyDirection)
    }
  }, [i18n])

  const handleLanguageToggle = () => {
    const nextLanguage = i18n.language === 'ar' ? 'en' : 'ar'
    i18n.changeLanguage(nextLanguage)
  }

  const languageToggleLabel = i18n.language === 'ar'
    ? t('login.languageShortEnglish')
    : t('login.languageShortArabic')

  const userTypes = useMemo(() => ([
    {
      id: "pet_guardian",
      title: t("register.pet_guardian"),
      description: t('selectUserType.cards.petGuardian.description'),
      image: "/images/landing/men-with-dog.png",
      borderColor: "#8548A8",
      accentColor: "#8548A8"
    },
    {
      id: "veterinarian",
      title: t("register.veterinarian"),
      description: t('selectUserType.cards.veterinarian.description'),
      image: "/images/landing/vet.png",
      borderColor: "#00D4B3",
      accentColor: "#00D4B3"
    },
    {
      id: "service_provider",
      title: t("register.service_provider"),
      description: t('selectUserType.cards.serviceProvider.description'),
      image: "/images/landing/service-provider.png",
      borderColor: "#D65745",
      accentColor: "#D65745"
    },
    {
      id: "firm",
      title: t("register.roles.firm.title"),
      description: t('selectUserType.cards.firm.description'),
      image: "/images/landing/landing-logo.png",
      borderColor: "#000080",
      accentColor: "#000080"
    },
  ]), [t])

  const handleCardClick = (typeId) => {
    navigate(`/auth/register/${typeId}`)
  }

  const handleBack = () => {
    navigate("/")
  }

  return (
    <div
      className="select-user-type-container"
      dir={isRTL ? 'rtl' : 'ltr'}
      lang={i18n.language}
      style={{
        width: "100vw",
        height: "100vh",
        background: "#FAFAFA",
        position: "fixed",
        top: 0,
        left: 0,
        fontFamily: '"Red Hat Display", sans-serif',
        overflow: "auto",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        direction: isRTL ? 'rtl' : 'ltr'
      }}
    >
      {/* Header Component - Inline */}
      <div
        className="select-user-type-header"
        style={{
          position: "absolute",
          top: "0",
          left: "0",
          width: "100%",
          height: "100px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "0 60px",
          zIndex: 10,
          boxSizing: "border-box",
          direction: isRTL ? 'rtl' : 'ltr'
        }}
      >
        <button
          type="button"
          onClick={handleBack}
          style={{
            background: "rgba(31, 41, 55, 0.08)",
            border: "none",
            borderRadius: "999px",
            padding: "10px 20px",
            fontFamily: '"Red Hat Display", sans-serif',
            fontSize: "14px",
            fontWeight: 500,
            color: "#1F2937",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            transition: "background 0.2s ease",
            flexDirection: isRTL ? 'row-reverse' : 'row'
          }}
        >
          <span aria-hidden="true">{isRTL ? '↩' : '↪'}</span>
          {t('selectUserType.actions.back')}
        </button>

        {/* Logo */}
        <div
          style={{
            width: '230px',
            height: '90px',
            backgroundImage: "url(/icons/navbar/Sulhafa_Beta_Logo.png)",
            backgroundSize: "contain",
            backgroundRepeat: "no-repeat",
            backgroundPosition: "center",
            cursor: "pointer",
          }}
          onClick={() => navigate("/")}
          aria-label={t('selectUserType.actions.home')}
        ></div>

        <button
          type="button"
          onClick={handleLanguageToggle}
          style={{
            background: "#1F2937",
            color: "#FFFFFF",
            border: "none",
            borderRadius: "999px",
            padding: "10px 20px",
            fontFamily: '"Red Hat Display", sans-serif',
            fontSize: "14px",
            fontWeight: 500,
            cursor: "pointer",
            transition: "background 0.2s ease"
          }}
          aria-label={t('selectUserType.actions.languageToggle')}
        >
          {languageToggleLabel}
        </button>

      </div>

      {/* Title */}
      <div
        style={{
          textAlign: isRTL ? "right" : "center",
          marginTop: "140px",
          marginBottom: "40px",
          padding: "0 20px",
          maxWidth: "720px",
          display: "flex",
          flexDirection: "column",
          gap: "12px"
        }}
      >
        <h1
          className="select-user-type-title"
          style={{
            fontFamily: '"Red Hat Display", sans-serif',
            fontWeight: 600,
            fontSize: "36px",
            lineHeight: "1.4",
            color: "#1F2937",
            margin: 0
          }}
        >
          {t("selectUserType.title")}
        </h1>
        <p
          style={{
            fontFamily: '"Red Hat Display", sans-serif',
            fontWeight: 400,
            fontSize: "18px",
            lineHeight: "1.6",
            color: "#4B5563",
            margin: 0
          }}
        >
          {t('selectUserType.subtitle')}
        </p>
      </div>

      {/* User Type Cards */}
      <div
        className="select-user-type-cards"
        style={{
          display: "flex",
          flexDirection: isRTL ? "row-reverse" : "row",
          justifyContent: "center",
          alignItems: "stretch",
          gap: "40px",
          padding: "0 60px",
          boxSizing: "border-box",
          flexWrap: "wrap"
        }}
      >
        {userTypes.map((type) => (
          <div
            key={type.id}
            className="select-user-type-card"
            style={{
              width: "340px",
              minHeight: "520px",
              background: "#FFFFFF",
              border: `2px solid ${type.borderColor}`,
              borderRadius: "20px",
              position: "relative",
              cursor: "pointer",
              transition: "all 0.3s ease",
              boxShadow: "0 8px 25px rgba(0,0,0,0.08)",
              flexShrink: 0,
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "24px 24px 32px",
              textAlign: 'center'
            }}
            onClick={() => handleCardClick(type.id)}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-4px)"
              e.currentTarget.style.boxShadow = `0 12px 30px rgba(0,0,0,0.12), 0 0 0 1px ${type.borderColor}20`
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0)"
              e.currentTarget.style.boxShadow = "0 8px 25px rgba(0,0,0,0.08)"
            }}
            role="button"
            tabIndex={0}
            aria-label={t('selectUserType.cards.select', { role: type.title })}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault()
                handleCardClick(type.id)
              }
            }}
          >
            <img
              className="select-user-type-card-img"
              src={type.image || "/placeholder.svg"}
              alt={type.title}
              style={{
                width: "260px",
                height: "260px",
                objectFit: "contain",
                marginBottom: "24px",
                transform: isRTL ? 'scaleX(-1)' : 'none'
              }}
            />
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', alignItems: 'center' }}>
              <h3
                style={{
                  fontFamily: '"Red Hat Display", sans-serif',
                  fontWeight: 600,
                  fontSize: "26px",
                  lineHeight: "1.3",
                  color: type.accentColor,
                  margin: 0
                }}
              >
                {type.title}
              </h3>
              <p
                style={{
                  fontFamily: '"Red Hat Display", sans-serif',
                  fontWeight: 400,
                  fontSize: "16px",
                  lineHeight: "1.5",
                  color: "#4B5563",
                  margin: 0,
                  maxWidth: '260px'
                }}
              >
                {type.description}
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* Responsive Styles */}
      <style>
        {`
          /* Mobile Responsive Styles */
          @media (max-width: 1024px) {
            .select-user-type-header {
              padding: 0 40px !important;
            }
            
            .select-user-type-cards {
              gap: 24px !important;
              padding: 0 40px !important;
            }
            
            .select-user-type-card {
              width: 300px !important;
              min-height: 440px !important;
            }
            
            .select-user-type-card-img {
              width: 220px !important;
              height: 220px !important;
            }
          }
          
          @media (max-width: 768px) {
            .select-user-type-container {
              padding-top: 40px !important;
              padding-bottom: 60px !important;
              justify-content: flex-start !important;
            }
            
            .select-user-type-header {
              display: none !important;
            }
            
            .select-user-type-title {
              font-size: 28px !important;
              margin: 20px 0 16px 0 !important;
            }
            
            .select-user-type-cards {
              flex-direction: column !important;
              gap: 24px !important;
              padding: 0 24px !important;
              width: 100% !important;
            }
            
            .select-user-type-card {
              width: 100% !important;
              max-width: 320px !important;
              min-height: 360px !important;
              padding: 20px !important;
            }
            
            .select-user-type-card-img {
              width: 200px !important;
              height: 200px !important;
              margin-bottom: 16px !important;
            }
            
            .select-user-type-card h3 {
              font-size: 22px !important;
            }
          }
          
          @media (max-width: 480px) {
            .select-user-type-title {
              font-size: 24px !important;
            }
            
            .select-user-type-card {
              min-height: 320px !important;
              padding: 16px !important;
            }
            
            .select-user-type-card-img {
              width: 160px !important;
              height: 160px !important;
            }
            
            .select-user-type-card h3 {
              font-size: 20px !important;
            }
          }
        `}
      </style>
    </div>
  )
}

export default SelectUserType

